import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import {
  LayoutDashboard,
  FilePlus2,
  Files,
  Users,
  Building2,
  Mail,
  BarChart3,
  Settings,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/project/lib/auth";
import { cn } from "@/project/lib/utils";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/applications/new", label: "New Assistance", icon: FilePlus2 },
  { to: "/applications", label: "Applications", icon: Files },
  { to: "/members", label: "Members", icon: Users },
  { to: "/organizations", label: "Organizations", icon: Building2 },
  { to: "/letters", label: "Letters", icon: Mail },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/masters", label: "Masters & Settings", icon: Settings },
];

export function Shell({ children }: { children: ReactNode }) {
  const { user, loading, fullName, isAdmin, signOut } = useAuth();
  const router = useRouter();
  const pathname = useRouterState({ select: s => s.location.pathname });

  useEffect(() => {
    if (!loading && !user) {
      router.navigate({ to: "/auth" });
    }
  }, [loading, user, router]);

  if (loading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="no-print sticky top-0 hidden h-screen w-60 shrink-0 flex-col bg-sidebar text-sidebar-foreground md:flex">
        <div className="border-b border-sidebar-border px-5 py-4">
          <p className="font-display text-base font-semibold leading-tight text-sidebar-primary">
            Motisha Sadharmik
          </p>
          <p className="text-sidebar-foreground/70 text-xs">
            Bhakti Kendra · Assistance Desk
          </p>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map(item => {
            const path = item.to.split("?")[0]!;
            const matches = (p: string) =>
              p === "/"
                ? pathname === "/"
                : pathname === p || pathname.startsWith(p + "/");
            // only the most specific matching nav item stays highlighted
            const bestMatch = NAV.map(n => n.to.split("?")[0]!)
              .filter(matches)
              .sort((a, b) => b.length - a.length)[0];
            const active = matches(path) && bestMatch === path;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                  active
                    ? "bg-sidebar-primary font-medium text-sidebar-primary-foreground"
                    : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-sidebar-border p-3 text-xs">
          <p className="truncate font-medium">{fullName || user.email}</p>
          <p className="text-sidebar-foreground/60">
            {isAdmin ? "Administrator" : "Staff"}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            onClick={async () => {
              await signOut();
              router.navigate({ to: "/auth" });
            }}
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        <div className="w-full p-4 md:px-6 md:py-6">{children}</div>
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="no-print mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}
